/** Board geometry helpers. */
import { BOARD_SIZE, CENTER, PREMIUM_LAYOUT } from './constants';
import type { Board, BoardTile, Coord, PremiumKind } from './types';

/** A fresh empty BOARD_SIZE × BOARD_SIZE board. */
export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_SIZE }, () => Array<BoardTile | null>(BOARD_SIZE).fill(null));
}

/** True for integer coordinates inside the board (0..BOARD_SIZE-1 on both axes). */
export function inBounds(row: number, col: number): boolean {
  return (
    Number.isInteger(row) &&
    Number.isInteger(col) &&
    row >= 0 &&
    row < BOARD_SIZE &&
    col >= 0 &&
    col < BOARD_SIZE
  );
}

/** The premium printed on a square (whether or not it is covered). */
export function premiumAt(row: number, col: number): PremiumKind | null {
  if (!inBounds(row, col)) return null;
  return PREMIUM_LAYOUT[row]?.[col] ?? null;
}

export function isCenter(row: number, col: number): boolean {
  return row === CENTER && col === CENTER;
}

export function isBoardEmpty(board: Board): boolean {
  return board.every((row) => row.every((cell) => cell === null));
}

/**
 * Human square name: column letter A–O (left→right) + row 1–15 (top→bottom).
 * The centre square (7,7) is "H8".
 */
export function cellName(row: number, col: number): string {
  return `${String.fromCharCode(65 + col)}${row + 1}`;
}

/** Deep copy of a board (tiles copied too). */
export function cloneBoard(board: Board): Board {
  return board.map((row) => row.map((cell) => (cell ? { ...cell } : null)));
}

/** Orthogonal neighbours that are on the board (order: up, down, left, right). */
export function neighbors(row: number, col: number): Coord[] {
  const candidates: Coord[] = [
    { row: row - 1, col },
    { row: row + 1, col },
    { row, col: col - 1 },
    { row, col: col + 1 },
  ];
  return candidates.filter((c) => inBounds(c.row, c.col));
}

/** The tile on a square, or null when the square is empty or off the board. */
export function tileAt(board: Board, row: number, col: number): BoardTile | null {
  if (!inBounds(row, col)) return null;
  return board[row]?.[col] ?? null;
}
